import { useState } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { GetProductDataProductsModel } from "features/stock-ordering/core/domain/get-product-data.model";
import { dispatchOrderParam } from "features/stock-ordering/core/stock-ordering.params";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  openMessageModal,
  closeMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { useParams } from "react-router-dom";
import {
  ActionHeader,
  BannerIndicator,
  CancelAndConfirm,
  isQuantityEmpty,
  useActionAvailability,
} from "./stock-ordering-utils";
import { selectTableDataChange } from "../slices/tableDataChange.slice";
import { stockDispatchOrders } from "../slices/stock-dispatch-order.slice";
import { selectGetProductData } from "../slices/get-product-data.slice";
import { CheckCircle } from "lucide-react";

interface StockOrderProcessSupplierDispatchOrderProps {
  onClose: (close: boolean) => void;
}

interface TransportRouteAndDispatchProps {
  disableConfirmButton: boolean;
  handleActionDispatchButtonClicked: (route: string | null) => void;
}

export function StockOrderProcessSupplierDispatchOrder(
  props: StockOrderProcessSupplierDispatchOrderProps
) {
  /**
   * url param that contains order id
   */
  const { orderId } = useParams();

  /**
   * dispatch and selector user for sending and fetching request
   */
  const dispatch = useAppDispatch();
  const processProductDataChange = useAppSelector(selectTableDataChange);
  const productDataState = useAppSelector(selectGetProductData);

  const { data, status } = processProductDataChange;

  const { order_information, product_batch_data } = productDataState.data ?? {};

  /**
   * Hooks for availability
   */
  const dispatcherAccess = useActionAvailability(6).permission;
  const supplierAccess = useActionAvailability(5).permission;
  const { processId, actionAvailable } = useActionAvailability();

  /**
   * Trigger for dispatch order
   * @param route string | null - transport route
   */
  const handleDispatchButton = (route: string | null) => {
    dispatch(
      openMessageModal({
        message: `Confirming this action will move the order for the next process. Are you sure you want to proceed?`,
        buttons: [
          {
            color: "#CC5801",
            text: "Yes",
            onClick: () => {
              if (orderId && route) {
                const dispatchOrdersParamData: dispatchOrderParam = {
                  id: orderId,
                  data: {
                    transport: route,
                    productData:
                      data?.productData.map(
                        (product: GetProductDataProductsModel) => ({
                          id: product.id,
                          product_id: product.product_id,
                          order_qty: product.dispatch_qty,
                          dispatched_qty: product.dispatch_qty,
                          dispatch_subtotal: product.dispatch_subtotal,
                          total_cost: product.dispatch_subtotal,
                        })
                      ) ?? [],
                  },
                };

                dispatch(stockDispatchOrders(dispatchOrdersParamData));
                props.onClose(true);
                dispatch(closeMessageModal());
              }
            },
          },
          {
            color: "#22201A",
            text: "No",
            onClick: () => {
              dispatch(closeMessageModal());
            },
          },
        ],
      })
    );
  };

  if (order_information?.companyId === 1 && product_batch_data?.length !== 0) {
    return (
      <>
        <BannerIndicator
          visible={actionAvailable}
          content="Ready to dispatch? Click the product to mark it as dispatched once you’re ready to send it out."
          textColor="text-[#4A4A4A]"
          bgColor="bg-[#AEE1F7]"
        />
        <BannerIndicator
          visible={!actionAvailable}
          content="This order is awaiting supplier dispatch. You can monitor the status here once the supplier sends the product."
          textColor="text-[#4A4A4A]"
          bgColor="bg-[#AEE1F7]"
        />
      </>
    );
  }

  /**
   * Dispatch permission must have access and must have must access to current process
   *
   * @returns React | Element - transport and dispatch button
   */
  if (data?.productData) {
    const noQuantityCheck = isQuantityEmpty(data?.productData ?? [], processId);

    return (
      <>
        <ActionHeader
          primary={"Supplier Dispatch"}
          secondary="This section records when the supplier sends out the products, confirms the delivery route, and ensures the correct quantities are dispatched for transport."
          HeaderIcon={CheckCircle}
        />

        <BannerIndicator
          visible={!dispatcherAccess || !supplierAccess}
          content="Your order is awaiting supplier dispatch. Once dispatched, it will be on its way to the receiving store."
          textColor="text-[#4A4A4A]"
          bgColor="bg-[#AEE1F7]"
        />
        {dispatcherAccess && (
          <TransportRouteAndDispatch
            handleActionDispatchButtonClicked={handleDispatchButton}
            disableConfirmButton={noQuantityCheck}
          />
        )}
      </>
    );
  }

  return null;
}

/**
 * @author knhrvs - kenoresca
 * ======================================================
 * Transport Route and Dispatch button
 * ======================================================
 *
 * @param handleActionDispatchButtonClicked - handles on dispatch button click
 * @param disableConfirmButton - disables dispatch button
 * @returns React Element - route and dispatch button component
 */

const TransportRouteAndDispatch = (props: TransportRouteAndDispatchProps) => {
  /**
   * Destructure props
   */
  const { handleActionDispatchButtonClicked, disableConfirmButton } = props;

  /**
   *  State containers
   */
  const [transport, setTransport] = useState<string | null>(null);

  return (
    <div className="flex flex-col md:flex-row md:justify-between w-full font-[Roboto] space-y-3 px-1">
      <FormControl
        sx={{
          alignSelf: { md: "flex-end" },
        }}
      >
        <FormLabel
          sx={{ fontSize: "0.875rem", fontFamily: "Roboto" }}
          id="transport-route-label"
        >
          Transport Route
        </FormLabel>

        <RadioGroup
          onChange={(event, value) => setTransport(value)}
          value={transport}
          row
          aria-labelledby="transport-route"
        >
          <FormControlLabel
            value="1"
            control={<Radio size="small" />}
            label="Ground"
            sx={{ fontSize: "0.875rem", fontFamily: "Roboto" }}
            disableTypography
          />
          <FormControlLabel
            value="2"
            control={<Radio size="small" />}
            label="Ocean"
            sx={{ fontSize: "0.875rem", fontFamily: "Roboto" }}
            disableTypography
          />
          <FormControlLabel
            value="3"
            control={<Radio size="small" />}
            label="Air"
            sx={{ fontSize: "0.875rem", fontFamily: "Roboto" }}
            disableTypography
          />
        </RadioGroup>
      </FormControl>

      <CancelAndConfirm
        disableConfirm={transport === null || disableConfirmButton}
        confirmButtonName="Dispatch Order"
        handleConfirm={() => handleActionDispatchButtonClicked(transport)}
        visible
      />
    </div>
  );
};
